// api/vatAPI.js
import axiosInstance from './axiosConfig';

const vatAPI = {
  // Get VAT records for a client (supports fiscalYear, quarter, month, recordType)
  getByClient: async (clientId, params = {}) => {
    try {
      const response = await axiosInstance.get(`/vat-records/client/${clientId}`, { params });
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch VAT records';
    }
  },

  /**
   * ✅ Create VAT records (MONTHLY encoding)
   * payload: { fiscalYear, records: [{ month, recordType, grossAmount, vatAmount, netAmount, ... }], defaultMonth? }
   *
   * Usage:
   * await vatAPI.createRecords(clientId, { fiscalYear: 2026, records: [...] })
   */
  createRecords: async (clientId, payload, fiscalYearNum) => {
    try {
      if (!clientId) throw 'Client ID is required';

      if (!fiscalYearNum || isNaN(fiscalYearNum)) throw 'Fiscal year is required';

      const records = payload?.records;
      if (!Array.isArray(records) || records.length === 0) throw 'Records are required';

      const cleanPayload = {
        fiscalYear: fiscalYearNum,
        records,
        ...(payload?.defaultMonth ? { defaultMonth: parseInt(payload.defaultMonth, 10) } : {}),
      };

      const response = await axiosInstance.post(`/vat-records/client/${clientId}`, cleanPayload);
      return response.data;
    } catch (error) {
      // Per-line errors (`errors: [{ line, fields }]`) can't ride on a thrown
      // string, so that case throws an Error carrying them for the grid to
      // place on rows. Every other case throws exactly what it did before.
      const lineErrors = error.response?.data?.errors;
      if (Array.isArray(lineErrors) && lineErrors.length) {
        throw Object.assign(new Error(error.response.data.message || 'Some lines were rejected'), {
          response: error.response,
          lineErrors,
        });
      }
      throw error.response?.data?.message || error || 'Failed to create VAT records';
    }
  },

  update: async (recordId, recordData) => {
    try {
      const response = await axiosInstance.put(`/vat-records/${recordId}`, recordData);
      console.log('[vatAPI.update] response.data raw:', response?.data);
      console.log('[vatAPI.update] response.data.data:', response?.data?.data);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to update VAT record';
    }
  },

  delete: async (recordId) => {
    try {
      const response = await axiosInstance.delete(`/vat-records/${recordId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to delete VAT record';
    }
  },

  // ✅ Quarter Summary for VAT computation
  getQuarterSummary: async (clientId, params = {}) => {
    try {
      const response = await axiosInstance.get(`/vat-records/quarter-summary/client/${clientId}`, { params });
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch VAT quarter summary';
    }
  },

  // ✅ Save/Update quarter adjustment (Excess from previous quarter)
  saveQuarterAdjustment: async (clientId, payload) => {
    try {
      const response = await axiosInstance.post(`/vat-records/clients/${clientId}/quarter-adjustment`, payload);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to save VAT quarter adjustment';
    }
  },

  getQuarterAdjustment: async (clientId, params = {}) => {
    try {
      const response = await axiosInstance.get(`/vat-records/clients/${clientId}/quarter-adjustment`, { params });
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch VAT quarter adjustment';
    }
  },

  postToGL: async (clientId, payload) => {
    try {
      const response = await axiosInstance.post(`/vat/clients/${clientId}/post-to-gl`, payload);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to post VAT records to GL';
    }
  },

  adjustGLRecord: async (clientId, payload) => {
    try {
      const response = await axiosInstance.post(`/vat/clients/${clientId}/adjust-gl-record`, payload);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to adjust VAT GL record';
    }
  },

  getGLAuditTrail: async (clientId, recordId) => {
    try {
      const response = await axiosInstance.get(`/vat/clients/${clientId}/records/${recordId}/gl-audit`);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch VAT GL audit trail';
    }
  },

  getForm2306ByClient: async (clientId, params = {}) => {
    try {
      if (!clientId) throw new Error('Client ID is required');

      const res = await axiosInstance.get(`/form-2306/client/${clientId}`, { params });

      // Backend shape:
      // { success: true, data: { records, totals, periods, pagination } }
      const records = res?.data?.data?.records || [];
      const totals = res?.data?.data?.totals || { totalBase: 0, totalTaxWithheld: 0, count: 0 };
      const periods = res?.data?.data?.periods || [];
      const pagination = res?.data?.data?.pagination || null;

      return {
        success: true,
        data: {
          records,
          totals,
          periods,
          pagination,
        },
      };
    } catch (error) {
      console.error('Form 2306 API Error:', error.response?.data || error.message);
      throw error.response?.data?.message || error.message || 'Failed to fetch Form 2306 records';
    }
  },

  calculateQuarterFromMonth: (month) => {
    const m = parseInt(month, 10);
    if (m <= 3) return 1;
    if (m <= 6) return 2;
    if (m <= 9) return 3;
    return 4;
  },

  getFiscalYearOptions: (yearsRange = 2) => {
    const currentYear = new Date().getFullYear();
    const options = [];
    for (let i = -yearsRange; i <= yearsRange; i++) {
      const year = currentYear + i;
      options.push({ value: year, label: year.toString(), isCurrent: i === 0 });
    }
    return options;
  },

  getQuarterOptions: () => [
    { value: 1, label: 'Q1 (Jan - Mar)' },
    { value: 2, label: 'Q2 (Apr - Jun)' },
    { value: 3, label: 'Q3 (Jul - Sep)' },
    { value: 4, label: 'Q4 (Oct - Dec)' },
  ],

  getMonthOptions: () => [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ],
};

export default vatAPI;
